import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, ShieldCheck, User as UserIcon, MagnifyingGlass, ClockCounterClockwise, UsersFour, ArrowsClockwise, CheckCircle, XCircle, MinusCircle, EnvelopeSimple } from "@phosphor-icons/react";
import { api, useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/PasswordInput";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const TILE_OPTIONS = [
  { key: "produzione", label: "Produzione", color: "#9F1239" },
  { key: "contabilita", label: "Contabilità", color: "#38BDF8" },
  { key: "tesoreria", label: "Tesoreria", color: "#E9A23B" },
  { key: "risorice", label: "Risorice", color: "#E05E3F" },
  { key: "posta", label: "Posta", color: "#F97316" },
  { key: "paghe", label: "Paghe", color: "#6366F1" },
  { key: "sohelp", label: "So.Help", color: "#10B981" },
  { key: "paqway", label: "PAQ.WAY", color: "#F43F5E" },
];
const ALL_TILES = TILE_OPTIONS.map((t) => t.key);

const emptyForm = { email: "", password: "", name: "", role: "user", tiles: ALL_TILES, comm_email: "", comm_enabled: true };

export default function AdminPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [view, setView] = useState("users");
  const [history, setHistory] = useState([]);
  const [historySearch, setHistorySearch] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);

  const handleSyncAll = async () => {
    setSyncingAll(true);
    try {
      const { data } = await api.post("/admin/users/sync-all");
      const withFailures = data.outcomes.filter((o) => o.failed_areas.length > 0);
      if (data.failed === 0) {
        toast.success(`Sincronizzati ${data.users} utenti verso le app collegate (${data.ok} invii riusciti)`);
      } else {
        toast.warning(`Sincronizzazione completata: ${data.ok} riusciti, ${data.failed} falliti`, {
          description: withFailures.slice(0, 3).map((o) => `${o.email}: ${o.failed_areas.join(", ")}`).join(" — "),
        });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSyncingAll(false);
      loadUsers();
    }
  };

  const handleSync = async (u) => {
    setSyncing(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/sync`);
      const ok = data.results.filter((r) => r.ok).map((r) => r.area);
      const ko = data.results.filter((r) => !r.ok);
      if (ko.length === 0) {
        toast.success(`Profilo sincronizzato in: ${ok.join(", ")}`);
      } else {
        toast.warning(`Sincronizzato: ${ok.join(", ") || "nessuna app"} — Fallito: ${ko.map((r) => r.area).join(", ")}`, {
          description: ko[0]?.detail || "Verifica che l'endpoint /api/sso/provision sia attivo nelle app di destinazione.",
        });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSyncing(null);
      loadUsers();
    }
  };

  const filteredUsers = users.filter((u) =>
    (u.email.toLowerCase().includes(search.toLowerCase().trim()) ||
     (u.name || "").toLowerCase().includes(search.toLowerCase().trim())) &&
    (roleFilter === "all" || u.role === roleFilter)
  );

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/admin/login-history");
      setHistory(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => { if (view === "history") loadHistory(); }, [view]);

  const filteredHistory = history.filter((h) =>
    h.email.toLowerCase().includes(historySearch.toLowerCase().trim())
  );

  const openAdd = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ email: u.email, password: "", name: u.name || "", role: u.role, tiles: u.tiles || ALL_TILES, comm_email: u.comm_email || u.email, comm_enabled: u.comm_enabled !== false }); setDialogOpen(true); };

  const toggleTile = (key) => {
    setForm((f) => ({
      ...f,
      tiles: f.tiles.includes(key) ? f.tiles.filter((t) => t !== key) : [...f.tiles, key],
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const commFields = form.role === "master"
        ? { comm_enabled: !!form.comm_enabled, ...(form.comm_email.trim() ? { comm_email: form.comm_email.trim() } : {}) }
        : {};
      if (editing) {
        const payload = { email: form.email, name: form.name, role: form.role, tiles: form.tiles, ...commFields };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/users/${editing.id}`, payload);
        toast.success("Utente aggiornato");
      } else {
        await api.post("/admin/users", { email: form.email, password: form.password, name: form.name, role: form.role, tiles: form.tiles, ...commFields });
        toast.success("Utente creato");
      }
      setDialogOpen(false);
      loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/users/${deleting.id}`);
      toast.success("Utente eliminato");
      setDeleting(null);
      loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="admin-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10 animate-fade-up">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2">Pannello Admin</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">
              {view === "users" ? "Gestione utenti" : "Storico accessi"}
            </h1>
            <p className="text-sm text-slate-400 mt-3">
              {view === "users" ? `${users.length} utenti registrati` : `${history.length} accessi registrati`}
            </p>
          </div>
          {view === "users" && (
            <div className="flex flex-wrap gap-3">
              <Button
                data-testid="sync-all-button"
                onClick={handleSyncAll}
                disabled={syncingAll}
                variant="ghost"
                className="h-11 px-6 border border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full font-semibold text-sm"
              >
                <ArrowsClockwise size={16} className={`mr-1.5 ${syncingAll ? "animate-spin" : ""}`} />
                {syncingAll ? "Sincronizzazione..." : "Sincronizza tutti"}
              </Button>
              <Button
                data-testid="add-user-button"
                onClick={openAdd}
                className="bg-white text-black hover:bg-gray-200 rounded-full h-11 px-6 font-semibold text-sm"
              >
                <Plus size={18} className="mr-1" /> Aggiungi utente
              </Button>
            </div>
          )}
        </div>

        <div className="inline-flex p-1 rounded-full border border-white/10 bg-surface mb-8 animate-fade-up" style={{ animationDelay: "30ms" }}>
          <button
            data-testid="users-tab-button"
            onClick={() => setView("users")}
            className={`inline-flex items-center gap-2 px-5 h-9 rounded-full text-xs font-semibold tracking-wide uppercase transition-colors duration-200 ${view === "users" ? "bg-white text-black" : "text-white/50 hover:text-white"}`}
          >
            <UsersFour size={15} weight="duotone" /> Utenti
          </button>
          <button
            data-testid="history-tab-button"
            onClick={() => setView("history")}
            className={`inline-flex items-center gap-2 px-5 h-9 rounded-full text-xs font-semibold tracking-wide uppercase transition-colors duration-200 ${view === "history" ? "bg-white text-black" : "text-white/50 hover:text-white"}`}
          >
            <ClockCounterClockwise size={15} weight="duotone" /> Storico accessi
          </button>
        </div>

        {view === "users" && (<>
        <div className="flex flex-col sm:flex-row gap-3 mb-3 animate-fade-up" style={{ animationDelay: "50ms" }}>
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              data-testid="user-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per email..."
              className="h-11 pl-10 bg-surface border-white/10 text-white placeholder:text-white/25 focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger data-testid="role-filter-select" className="h-11 w-full sm:w-48 bg-surface border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-surface border-white/10 text-white">
              <SelectItem value="all" data-testid="filter-option-all">Tutti i ruoli</SelectItem>
              <SelectItem value="master" data-testid="filter-option-master">Master</SelectItem>
              <SelectItem value="admin" data-testid="filter-option-admin">Admin</SelectItem>
              <SelectItem value="user" data-testid="filter-option-user">Utente</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <p className="text-[11px] text-slate-500 mb-6 flex items-center gap-4 animate-fade-up" data-testid="sync-legend" style={{ animationDelay: "70ms" }}>
          <span className="inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" className="text-emerald-400" /> sincronizzata</span>
          <span className="inline-flex items-center gap-1"><XCircle size={12} weight="fill" className="text-red-400" /> errore ultima sync</span>
          <span className="inline-flex items-center gap-1"><MinusCircle size={12} className="text-slate-500" /> mai sincronizzata</span>
        </p>

        <div className="border border-surface-border rounded-lg bg-surface overflow-hidden animate-fade-up" style={{ animationDelay: "100ms" }}>
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-xs tracking-[0.2em] uppercase text-white/40 px-6">Utente</TableHead>
                <TableHead className="text-xs tracking-[0.2em] uppercase text-white/40">Ruolo</TableHead>
                <TableHead className="text-xs tracking-[0.2em] uppercase text-white/40">App collegate e sync</TableHead>
                <TableHead className="text-xs tracking-[0.2em] uppercase text-white/40">Creato il</TableHead>
                <TableHead className="text-xs tracking-[0.2em] uppercase text-white/40">Ultimo accesso</TableHead>
                <TableHead className="text-xs tracking-[0.2em] uppercase text-white/40 text-right px-6">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 && (
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableCell colSpan={6} className="px-6 py-10 text-center text-sm text-slate-400" data-testid="no-users-message">
                    Nessun utente trovato
                  </TableCell>
                </TableRow>
              )}
              {filteredUsers.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`} className="border-white/5 hover:bg-white/[0.02]">
                  <TableCell className="px-6 text-sm">
                    <div className="text-white font-medium">
                      {u.name || <span className="text-white/30">—</span>}
                      {u.id === me?.id && <span className="ml-2 text-[10px] text-white/40 uppercase tracking-wider">(tu)</span>}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">{u.email}</div>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border ${u.role === "master" ? "border-amber-400/50 text-amber-400" : u.role === "admin" ? "border-white/30 text-white" : "border-white/10 text-slate-400"}`}>
                      {u.role !== "user" ? <ShieldCheck size={13} weight="duotone" /> : <UserIcon size={13} weight="duotone" />}
                      {u.role}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1.5 max-w-[280px]" data-testid={`user-tiles-${u.email}`}>
                      {TILE_OPTIONS.filter((t) => !u.tiles || u.tiles.includes(t.key)).map((t) => {
                        const s = u.sync_status?.[t.key];
                        const stateCls = s?.ok
                          ? "border-emerald-400/40 text-emerald-400 bg-emerald-400/5"
                          : s
                          ? "border-red-500/40 text-red-400 bg-red-500/5"
                          : "border-white/10 text-slate-500";
                        const tooltip = s
                          ? `${s.ok ? "Sincronizzata" : "Sync fallita"} — ${new Date(s.at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}${s.detail ? ` — ${s.detail}` : ""}`
                          : "Mai sincronizzata";
                        return (
                          <span
                            key={t.key}
                            title={tooltip}
                            className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${stateCls}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: t.color }} />
                            {t.label}
                            {s?.ok ? <CheckCircle size={11} weight="fill" /> : s ? <XCircle size={11} weight="fill" /> : <MinusCircle size={11} />}
                          </span>
                        );
                      })}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-400">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : "—"}
                  </TableCell>
                  <TableCell className="text-sm text-slate-400" data-testid={`user-last-login-${u.email}`}>
                    {u.last_login
                      ? new Date(u.last_login).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
                      : "Mai"}
                  </TableCell>
                  <TableCell className="text-right px-6">
                    <div className="flex justify-end gap-2">
                      {u.role === "master" && (
                        <span
                          data-testid={`comm-status-${u.email}`}
                          title={u.comm_enabled ? `Comunicazioni di servizio attive → ${u.comm_email || u.email}` : "Comunicazioni di servizio disabilitate"}
                          className={`h-8 w-8 rounded-full inline-flex items-center justify-center ${u.comm_enabled ? "text-emerald-400" : "text-white/20"}`}
                        >
                          <EnvelopeSimple size={16} weight={u.comm_enabled ? "fill" : "regular"} />
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`sync-user-${u.email}`}
                        disabled={syncing === u.id}
                        onClick={() => handleSync(u)}
                        title="Sincronizza il profilo nelle app collegate"
                        className="text-sky-400/70 hover:text-sky-400 hover:bg-sky-400/10 h-8 w-8 p-0 rounded-full"
                      >
                        <ArrowsClockwise size={16} className={syncing === u.id ? "animate-spin" : ""} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`edit-user-${u.email}`}
                        onClick={() => openEdit(u)}
                        className="text-white/60 hover:text-white hover:bg-white/5 h-8 w-8 p-0 rounded-full"
                      >
                        <PencilSimple size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`delete-user-${u.email}`}
                        disabled={u.id === me?.id}
                        onClick={() => setDeleting(u)}
                        className="text-red-500/60 hover:text-red-500 hover:bg-red-500/10 h-8 w-8 p-0 rounded-full disabled:opacity-20"
                      >
                        <Trash size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        </>)}

        {view === "history" && (<>
        <div className="relative max-w-md mb-6 animate-fade-up" style={{ animationDelay: "50ms" }}>
          <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            data-testid="history-search-input"
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
            placeholder="Filtra per email..."
            className="h-11 pl-10 bg-surface border-white/10 text-white placeholder:text-white/25 focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40"
          />
        </div>

        <div className="border border-surface-border rounded-lg bg-surface overflow-hidden animate-fade-up" style={{ animationDelay: "100ms" }}>
          <Table data-testid="login-history-table">
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-xs tracking-[0.2em] uppercase text-white/40 px-6">Email</TableHead>
                <TableHead className="text-xs tracking-[0.2em] uppercase text-white/40">Data e ora</TableHead>
                <TableHead className="text-xs tracking-[0.2em] uppercase text-white/40 px-6">Indirizzo IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredHistory.length === 0 && (
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableCell colSpan={3} className="px-6 py-10 text-center text-sm text-slate-400" data-testid="no-history-message">
                    Nessun accesso registrato
                  </TableCell>
                </TableRow>
              )}
              {filteredHistory.map((h) => (
                <TableRow key={h.id} className="border-white/5 hover:bg-white/[0.02]">
                  <TableCell className="px-6 text-sm text-white font-medium">{h.email}</TableCell>
                  <TableCell className="text-sm text-slate-400">
                    {new Date(h.timestamp).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                  </TableCell>
                  <TableCell className="px-6 text-sm text-slate-400 font-mono">{h.ip || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        </>)}
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-surface border-white/10 text-white sm:max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-xl">
              {editing ? "Modifica utente" : "Nuovo utente"}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              {editing ? "Aggiorna i dati e le aree visibili dell'utente." : "Imposta credenziali, ruolo e aree visibili."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-5 mt-2">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Nome</Label>
              <Input
                type="text"
                data-testid="user-form-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nome"
                className="h-11 bg-transparent border-white/10 text-white placeholder:text-white/25 focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Email</Label>
              <Input
                type="email"
                required
                data-testid="user-form-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="h-11 bg-transparent border-white/10 text-white focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">
                Password {editing && <span className="text-white/30 normal-case tracking-normal">(lascia vuoto per non cambiare)</span>}
              </Label>
              <PasswordInput
                required={!editing}
                minLength={6}
                data-testid="user-form-password-input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="h-11 bg-transparent border-white/10 text-white focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Ruolo</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-form-role-select" className="h-11 bg-transparent border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-surface border-white/10 text-white">
                  <SelectItem value="user" data-testid="role-option-user">Utente</SelectItem>
                  <SelectItem value="admin" data-testid="role-option-admin">Admin</SelectItem>
                  {me?.role === "master" && <SelectItem value="master" data-testid="role-option-master">Master</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            {form.role === "master" && (
              <div className="space-y-2 p-3 rounded-md border border-violet-400/25 bg-violet-400/[0.04]">
                <div className="flex items-center justify-between">
                  <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Email di comunicazione</Label>
                  <label className="flex items-center gap-2 cursor-pointer text-xs text-white/70" data-testid="comm-enabled-checkbox">
                    <Checkbox
                      checked={!!form.comm_enabled}
                      onCheckedChange={(v) => setForm({ ...form, comm_enabled: !!v })}
                      className="border-white/30 data-[state=checked]:bg-white data-[state=checked]:text-black"
                    />
                    {form.comm_enabled ? "Abilitata" : "Disabilitata"}
                  </label>
                </div>
                <Input
                  type="email"
                  data-testid="comm-email-input"
                  value={form.comm_email}
                  disabled={!form.comm_enabled}
                  onChange={(e) => setForm({ ...form, comm_email: e.target.value })}
                  placeholder="email@dominio.it"
                  className="h-11 bg-transparent border-white/10 text-white focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40 disabled:opacity-40"
                />
                <p className="text-[11px] text-white/35">Riceve le comunicazioni di servizio di PAQ (backup, avvisi, report).</p>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Aree visibili nel menu</Label>
              <div className="grid grid-cols-2 gap-2 pt-1">
                {TILE_OPTIONS.map((t) => (
                  <label
                    key={t.key}
                    data-testid={`tile-checkbox-${t.key}`}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-md border border-white/10 cursor-pointer hover:bg-white/[0.03] transition-colors duration-200"
                  >
                    <Checkbox
                      checked={form.tiles.includes(t.key)}
                      onCheckedChange={() => toggleTile(t.key)}
                      className="border-white/30 data-[state=checked]:bg-white data-[state=checked]:text-black"
                    />
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }} />
                    <span className="text-sm">{t.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button
                type="submit"
                disabled={saving}
                data-testid="user-form-submit-button"
                className="w-full h-11 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm"
              >
                {saving ? "Salvataggio..." : editing ? "Salva modifiche" : "Crea utente"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent className="bg-surface border-white/10 text-white" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Eliminare l'utente?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Stai per eliminare <span className="text-white">{deleting?.email}</span>. Questa azione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button" className="bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white rounded-full">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-button"
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
